/*
  localSoloGameDB.ts
  Copyright (c) 2024-present Human Cube Inc.

  Loading and saving of local only solo games to the browser based indexedDB.
*/

import Dexie, { type EntityTable } from 'dexie';
import { Game } from '../bfcore/gameTypes';


interface SoloGameDBEntry {
  id: number;  // The gameID used as primary key.
  uid: number;
  game: Game;
}

const soloGameDB = new Dexie('FriendsDatabase') as Dexie & {
  soloGames: EntityTable<
    SoloGameDBEntry,
    'id' // primary key "id" (for the typings only)
  >;
};

// Schema declaration:
soloGameDB.version(1).stores({
  soloGames: 'id, uid' // primary key "id" (for the runtime!), and index uid.
});


export async function soloGameSave (uid: number, game: Game): Promise<boolean> {
  // Returns true on success, false on error.
  try {
    // Use 'put' to create new entry or to replace existing:
    await soloGameDB.soloGames.put({
      id: game._id,
      uid,
      game,
    });
    // console.log('Successfully saved solo game!', id);
    return true;
  } catch (error) {
    console.error('Failed to save solo game!');
    return false;
  }
};


export async function soloGameFetch (gameID: number): Promise<Game | false> {
  // Returns Game on success.
  try {
    const game = await soloGameDB.soloGames.get(gameID);
    if(game) {
      // console.log('Successfully fetched solo game!', gameID);
      return game.game;
    }
    console.error('Fetch solo game was not found.');
  } catch (error) {
    console.error('Failed to fetch solo game!', error);
  }
  return false;
};


export async function soloGameDelete (gameID: number) {
  // Delete local solo game.
  try {
    await soloGameDB.soloGames.delete(gameID);
  } catch (error) {
  }
};


export type { SoloGameDBEntry };
export { soloGameDB };
